import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Building2, 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight, 
  Upload,
  ImageIcon,
  Loader2,
  Plus,
  X,
  SkipForward,
  AlertCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Get user from localStorage
const getUser = () => {
  try {
    const storedUser = localStorage.getItem('exlva_dare_user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const SetupWizard = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [success, setSuccess] = useState('');
  const [user] = useState(getUser);
  
  // Track which steps were skipped
  const [skippedSteps, setSkippedSteps] = useState({
    1: false,
    2: false,
    3: false
  });
  
  // Step 1: Logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Step 2: Departments
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [customDepartments, setCustomDepartments] = useState([]);
  const [newCustomDept, setNewCustomDept] = useState('');
  
  // Step 3: Purposes
  const [purposeCategories, setPurposeCategories] = useState([]);
  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const totalSteps = 3;
  
  // Fetch departments and purpose categories on mount
  useEffect(() => {
    fetchDepartments();
    fetchPurposeCategories();
  }, []);
  
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/departments`);
      setAvailableDepartments(response.data.departments || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };
  
  const fetchPurposeCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/purpose-categories`);
      const categories = response.data.categories || [];
      setPurposeCategories(categories);
      // Expand all categories by default
      const expanded = {};
      categories.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (err) {
      console.error('Error fetching purpose categories:', err);
    }
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid image file (PNG, JPG, GIF, WebP, or SVG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };
  
  const uploadLogo = async () => {
    if (!logoFile || !user?.id) return null;
    
    setUploadingLogo(true);
    setError('');
    
    try {
      // Get the token for authenticated requests
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Determine content type from file
      const contentType = logoFile.type || 'image/png';
      
      // Get presigned URL with proper content type
      const urlResponse = await axios.post(
        `${API_URL}/setup/upload-logo?content_type=${encodeURIComponent(contentType)}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const { upload_url, logo_url } = urlResponse.data;
      
      if (!upload_url) {
        throw new Error('Failed to get upload URL from server');
      }
      
      // Upload to S3 with correct content type
      await axios.put(upload_url, logoFile, {
        headers: { 
          'Content-Type': contentType
        }
      });
      
      setLogoUrl(logo_url);
      setUploadingLogo(false);
      return logo_url;
    } catch (err) {
      console.error('Error uploading logo:', err);
      setUploadingLogo(false);
      
      // Provide specific error message
      if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'Logo upload failed - S3 storage may not be configured');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 500) {
        setError('Server error during logo upload. Please try again later.');
      } else {
        setError(err.message || 'Failed to upload logo. Please try again.');
      }
      return null;
    }
  };
  
  const toggleDepartment = (dept) => {
    setSelectedDepartments(prev => 
      prev.includes(dept) 
        ? prev.filter(d => d !== dept)
        : [...prev, dept]
    );
  };
  
  const addCustomDepartment = () => {
    if (newCustomDept.trim() && !customDepartments.includes(newCustomDept.trim())) {
      setCustomDepartments([...customDepartments, newCustomDept.trim()]);
      setNewCustomDept('');
    }
  };
  
  const removeCustomDepartment = (dept) => {
    setCustomDepartments(customDepartments.filter(d => d !== dept));
  };
  
  const togglePurpose = (purposeId) => {
    setSelectedPurposes(prev => 
      prev.includes(purposeId)
        ? prev.filter(p => p !== purposeId)
        : [...prev, purposeId]
    );
  };
  
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  const selectAllInCategory = (category) => {
    const categoryItemIds = category.items.map(item => item.id);
    const allSelected = categoryItemIds.every(id => selectedPurposes.includes(id));
    
    if (allSelected) {
      setSelectedPurposes(prev => prev.filter(id => !categoryItemIds.includes(id)));
    } else {
      setSelectedPurposes(prev => [...new Set([...prev, ...categoryItemIds])]);
    }
  };
  
  const handleSkip = async () => {
    setError('');
    setSkippedSteps(prev => ({ ...prev, [currentStep]: true }));
    
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      // On last step, skip means complete with empty selection
      await handleComplete();
    }
  };
  
  const handleNext = async () => {
    setError('');
    setErrorDetails('');
    setSkippedSteps(prev => ({ ...prev, [currentStep]: false }));
    
    if (currentStep === 1) {
      // Try to upload logo if one was selected
      if (logoFile && !logoUrl) {
        try {
          await uploadLogo();
        } catch (err) {
          // Continue without logo if upload fails
          console.error('Logo upload failed, continuing without logo');
        }
      }
    }
    
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };
  
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
    setErrorDetails('');
  };
  
  const handleComplete = async () => {
    setError('');
    setErrorDetails('');
    
    // Get fresh user data from localStorage
    const currentUser = getUser();
    
    if (!currentUser?.id) {
      setError('User session not found. Please log in again.');
      setErrorDetails('User data not found in local storage.');
      return;
    }
    
    setLoading(true);
    try {
      const setupData = {
        organization_logo_url: logoUrl || null,
        organization_name: organizationName || 'My Organization',
        departments: selectedDepartments,
        custom_departments: customDepartments,
        selected_purposes: selectedPurposes
      };
      
      const response = await axios.post(
        `${API_URL}/setup/complete?user_id=${currentUser.id}`,
        setupData
      );
      
      if (response.data.success) {
        setSuccess('Setup completed successfully! Redirecting to dashboard...');
        
        // Notify parent component after a short delay
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Setup completion failed');
      }
      
    } catch (err) {
      console.error('Error completing setup:', err);
      
      // Provide specific error messages based on error type
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        setErrorDetails('Authentication token is invalid or expired.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to complete setup.');
        setErrorDetails('Access denied by server.');
      } else if (err.response?.status === 422) {
        setError('Invalid data submitted. Please check your entries.');
        setErrorDetails(JSON.stringify(err.response?.data?.detail || 'Validation error'));
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again in a moment.');
        setErrorDetails(`Server returned status ${err.response?.status}`);
      } else if (err.message === 'Network Error') {
        setError('Network connection error. Please check your internet connection.');
        setErrorDetails('Unable to reach the server.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to complete setup. Please try again.');
        setErrorDetails(err.response?.data?.message || '');
      }
      setLoading(false);
    }
  };
  
  const progressValue = (currentStep / totalSteps) * 100;
  
  // Check if current step can proceed (for non-skip next)
  const canProceed = () => {
    if (currentStep === 1) {
      return organizationName.trim().length > 0;
    }
    if (currentStep === 2) {
      return selectedDepartments.length > 0 || customDepartments.length > 0;
    }
    if (currentStep === 3) {
      return selectedPurposes.length > 0;
    }
    return true;
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8" style={{ backgroundColor: 'var(--theme-background, #f8fafc)' }}>
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/exlva-logo.png" 
            alt="Exlva" 
            className="h-12 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--theme-text-primary, #1e293b)' }}>
            Welcome to DARe
          </h1>
          <p style={{ color: 'var(--theme-text-secondary, #64748b)' }}>
            Complete the setup to start using the platform
          </p>
        </div>
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm" style={{ color: 'var(--theme-text-secondary, #64748b)' }}>
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm" style={{ color: 'var(--theme-text-secondary, #64748b)' }}>
              {Math.round(progressValue)}% Complete
            </span>
          </div>
          <Progress value={progressValue} className="h-2" />
          <div className="flex justify-between mt-3">
            <div className="flex flex-col items-start">
              <span className={`text-sm font-medium ${currentStep >= 1 ? 'text-sky-600' : 'text-gray-400'}`}>
                Organization
              </span>
              {skippedSteps[1] && <Badge variant="secondary" className="text-xs mt-1">Skipped</Badge>}
            </div>
            <div className="flex flex-col items-center">
              <span className={`text-sm font-medium ${currentStep >= 2 ? 'text-sky-600' : 'text-gray-400'}`}>
                Departments
              </span>
              {skippedSteps[2] && <Badge variant="secondary" className="text-xs mt-1">Skipped</Badge>}
            </div>
            <div className="flex flex-col items-end">
              <span className={`text-sm font-medium ${currentStep >= 3 ? 'text-sky-600' : 'text-gray-400'}`}>
                Data Usage
              </span>
              {skippedSteps[3] && <Badge variant="secondary" className="text-xs mt-1">Skipped</Badge>}
            </div>
          </div>
        </div>
        
        {/* Error/Success Messages */}
        {error && (
          <Alert className="mb-4 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="font-medium">{error}</div>
              {errorDetails && (
                <div className="text-sm mt-1 text-red-600">{errorDetails}</div>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => { setError(''); setErrorDetails(''); }}
              >
                Dismiss
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 border-green-200 bg-green-50">
            <AlertDescription className="text-green-800 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {success}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Step Content */}
        <Card className="shadow-lg border" style={{ borderColor: 'var(--theme-border, #e2e8f0)' }}>
          {/* Step 1: Organization & Logo */}
          {currentStep === 1 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Building2 className="h-5 w-5 text-sky-600" />
                  Organization Details
                </CardTitle>
                <CardDescription>
                  Enter your organization name and upload your logo (optional). You can skip this step and configure it later.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="orgName" className="text-base">Organization Name</Label>
                  <Input
                    id="orgName"
                    placeholder="e.g., City General Hospital"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    className="max-w-lg text-base py-3"
                  />
                  <p className="text-sm text-gray-500">This will be displayed in your dashboard header.</p>
                </div>
                
                <div className="space-y-3">
                  <Label className="text-base">Organization Logo (Optional)</Label>
                  <div className="flex items-start gap-6">
                    <div className="w-36 h-36 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                      ) : (
                        <ImageIcon className="h-14 w-14 text-gray-400" />
                      )}
                    </div>
                    <div className="space-y-3">
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                      <label htmlFor="logo-upload">
                        <Button variant="outline" className="cursor-pointer" asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            Choose Logo
                          </span>
                        </Button>
                      </label>
                      <p className="text-sm text-gray-500">
                        PNG, JPG up to 5MB. Recommended: 200x200px
                      </p>
                      {uploadingLogo && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}
          
          {/* Step 2: Departments */}
          {currentStep === 2 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Select Departments</CardTitle>
                <CardDescription>
                  Choose the departments you work with. You can also add custom departments. Skip if not applicable.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-2">
                {/* Selected count */}
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-sm px-3 py-1">
                    {selectedDepartments.length + customDepartments.length} selected
                  </Badge>
                </div>
                
                {/* Department grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-72 overflow-y-auto p-1">
                  {availableDepartments.map((dept) => (
                    <label
                      key={dept}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedDepartments.includes(dept)
                          ? 'border-sky-500 bg-sky-50 shadow-sm'
                          : 'border-gray-200 hover:border-sky-300 hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedDepartments.includes(dept)}
                        onCheckedChange={() => toggleDepartment(dept)}
                      />
                      <span className="text-sm font-medium">{dept}</span>
                    </label>
                  ))}
                </div>
                
                {/* Custom departments */}
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-base">Add Custom Department</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter department name"
                      value={newCustomDept}
                      onChange={(e) => setNewCustomDept(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addCustomDepartment()}
                      className="max-w-sm"
                    />
                    <Button variant="outline" onClick={addCustomDepartment}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  {customDepartments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {customDepartments.map((dept) => (
                        <Badge key={dept} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                          {dept}
                          <button onClick={() => removeCustomDepartment(dept)} className="ml-1 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </>
          )}
          
          {/* Step 3: Purpose Selection */}
          {currentStep === 3 && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Purpose of Data Usage</CardTitle>
                <CardDescription>
                  Select the purposes for which you will use the clinical data. This helps ensure compliance and proper data governance. You can skip and configure later.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                {/* Selected count */}
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-sm px-3 py-1">
                    {selectedPurposes.length} purposes selected
                  </Badge>
                </div>
                
                {/* Purpose categories */}
                <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                  {purposeCategories.map((category) => {
                    const categoryItemIds = category.items.map(item => item.id);
                    const selectedCount = categoryItemIds.filter(id => selectedPurposes.includes(id)).length;
                    const allSelected = selectedCount === categoryItemIds.length && categoryItemIds.length > 0;
                    
                    return (
                      <div key={category.id} className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--theme-border, #e2e8f0)' }}>
                        {/* Category header */}
                        <div 
                          className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                          onClick={() => toggleCategory(category.id)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{category.icon}</span>
                            <span className="font-medium">{category.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {selectedCount}/{category.items.length}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                selectAllInCategory(category);
                              }}
                              className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            >
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </Button>
                            <ChevronRight className={`h-4 w-4 transition-transform ${expandedCategories[category.id] ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Category items */}
                        {expandedCategories[category.id] && (
                          <div className="p-3 space-y-2">
                            {category.description && (
                              <p className="text-sm text-gray-500 mb-3">{category.description}</p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {category.items.map((item) => (
                                <label
                                  key={item.id}
                                  className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                                    selectedPurposes.includes(item.id)
                                      ? 'bg-sky-50'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <Checkbox
                                    checked={selectedPurposes.includes(item.id)}
                                    onCheckedChange={() => togglePurpose(item.id)}
                                  />
                                  <span className="text-sm">{item.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </>
          )}
          
          {/* Navigation */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 border-t" style={{ borderColor: 'var(--theme-border, #e2e8f0)' }}>
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
              className="w-full sm:w-auto"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* Skip button - show on all steps */}
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={loading}
                className="text-gray-500 hover:text-gray-700 flex-1 sm:flex-none"
              >
                <SkipForward className="h-4 w-4 mr-1" />
                {currentStep === totalSteps ? 'Skip & Complete' : 'Skip'}
              </Button>
              
              {currentStep < totalSteps ? (
                <Button 
                  onClick={handleNext} 
                  disabled={!canProceed()}
                  className="bg-sky-600 hover:bg-sky-700 text-white flex-1 sm:flex-none"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={handleComplete} 
                  disabled={loading}
                  className="bg-sky-600 hover:bg-sky-700 text-white flex-1 sm:flex-none"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete Setup
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>
        
        {/* Help text */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            You can always update these settings later from the <strong>Configuration</strong> menu.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
